import classNames from 'classnames';
import {useEffect, useRef, useState} from 'react';
import {usePulsoidClient} from '../../PulsoidClient';
import {ButtonV2} from '../../system/ButtonV2/ButtonV2';
import {HeartRateInteractor} from '../integrations/edit/integration/voicemod/HeartrateStatus';
import {OVRT} from './ovrt-helper';
import css from './OVRToolkit.module.scss';

type LoginState = 'checking' | 'loggedin' | 'anonymous';

export const OVRToolkitV2 = () => {
  let ovrt = useOVRT();
  let client = usePulsoidClient({noAuthRedirect: true});
  const [loginState, setLoginState] = useState<LoginState>('checking');

  useEffect(() => {
    const fetch = async () => {
      let userInfo = await client.getUserInfo();
      if (userInfo) {
        setLoginState('loggedin');
      } else {
        setLoginState('anonymous');
      }
    };

    fetch();
  });

  let body = <h4>Loading....</h4>;

  if (loginState == 'loggedin') {
    body = (
      <div className={css.container}>
        <h4> OVR Toolkit Setup </h4>
        <OVRStatus ovrt={ovrt} />
        <HeartRateStatus />
        <h5>Widgets</h5>
      </div>
    );
  }

  if (loginState == 'anonymous') {
    body = (
      <div>
        <h4>Login Required</h4>
        <ButtonV2
          type="primary-outline"
          href={`https://pulsoid.net/login?disableNavigation&redirect=https://pulsoid.net/ovr-toolkit/main`}
        >
          Go To Login
        </ButtonV2>
      </div>
    );
  }

  return <div className={css.wrapper}>{body}</div>;
};

type StatusState = 'connected' | 'disconnected';

const OVRStatus = ({ovrt}: {ovrt: OVRT | null}) => {
  const [ovrState, setOVRState] = useState<StatusState>('disconnected');

  useEffect(() => {
    if (ovrt) {
      setOVRState('connected');
    } else {
      setOVRState('disconnected');
    }
  }, [ovrt]);

  return (
    <div className={css.step}>
      <div>OVR</div>
      <div
        className={classNames({
          [css.badge_connected]: ovrState == 'connected',
          [css.badge_disconnected]: ovrState == 'disconnected',
        })}
      >
        {ovrState}
      </div>
    </div>
  );
};

const HeartRateStatus = () => {
  const [heartRateState, setHeartRateState] =
    useState<StatusState>('disconnected');

  return (
    <div className={css.step}>
      <HeartRateInteractor
        onWaitingData={() => {
          setHeartRateState('disconnected');
        }}
        onConnected={() => {
          setHeartRateState('connected');
        }}
      />
      <div>Heart Rate</div>
      <div
        className={classNames({
          [css.badge_connected]: heartRateState == 'connected',
          [css.badge_disconnected]: heartRateState == 'disconnected',
        })}
      >
        {heartRateState}
      </div>
    </div>
  );
};

const useOVRT = () => {
  let ref = useRef(new OVRT({function_queue: false}));
  let [ovrt, setOvrt] = useState<OVRT | null>(null);

  useEffect(() => {
    function ApiReadyListener() {
      setOvrt(ref.current);
    }
    window.addEventListener('api-ready', ApiReadyListener);

    return () => {
      window.removeEventListener('api-ready', ApiReadyListener);
    };
  });

  return ovrt;
};
